import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Download, Trash2, File } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    file_url: '',
    file_type: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, userRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/documents`),
        authFetch(`${BACKEND_URL}/api/auth/me`)
      ]);

      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData);
      }

      if (userRes.ok) {
        const userData = await userRes.json();
        setCurrentUser(userData);
      }
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Document added successfully');
        setDialogOpen(false);
        setFormData({ name: '', description: '', file_url: '', file_type: '' });
        fetchData();
      } else {
        toast.error('Failed to add document');
      }
    } catch (error) {
      toast.error('Failed to add document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await authFetch(`${BACKEND_URL}/api/documents/${docId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Document deleted');
        fetchData();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getFileIcon = (fileType) => {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return '📄';
    if (type.includes('doc')) return '📝';
    if (type.includes('sheet') || type.includes('xls')) return '📊';
    if (type.includes('image') || type.includes('png') || type.includes('jpg')) return '🖼️';
    return '📁';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
            <p className="text-slate-600 mt-1">Manage company documents and files</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-document-button" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-document-dialog">
              <DialogHeader>
                <DialogTitle>Add New Document</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Document Name *</Label>
                  <Input
                    id="name"
                    data-testid="doc-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter document name"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="doc-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter document description"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="file_url">File URL *</Label>
                  <Input
                    id="file_url"
                    type="url"
                    data-testid="doc-url-input"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    required
                    placeholder="https://example.com/document.pdf"
                  />
                </div>
                <div>
                  <Label htmlFor="file_type">File Type *</Label>
                  <Input
                    id="file_type"
                    data-testid="doc-type-input"
                    value={formData.file_type}
                    onChange={(e) => setFormData({ ...formData, file_type: e.target.value })}
                    required
                    placeholder="e.g., PDF, DOCX, XLSX"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-document-button">Add Document</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <Card key={doc.doc_id} className="card-hover" data-testid={`document-${doc.doc_id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl">{getFileIcon(doc.file_type)}</div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate">{doc.name}</CardTitle>
                      <p className="text-xs text-slate-500 mt-1">{doc.file_type}</p>
                    </div>
                  </div>
                  {currentUser?.role === 'admin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-doc-${doc.doc_id}`}
                      onClick={() => handleDelete(doc.doc_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {doc.description && (
                  <p className="text-sm text-slate-600">{doc.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {new Date(doc.created_at).toLocaleDateString()}
                  </span>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`download-doc-${doc.doc_id}`}
                  >
                    <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                      <Download className="w-4 h-4 mr-2" />
                      View
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
          {documents.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-12">
                <div className="text-center text-slate-500">
                  <File className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p>No documents available</p>
                  <p className="text-sm mt-2">Click "Add Document" to upload your first document</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}